import { useField, useFormikContext } from "formik";
import React, { useMemo, useState } from "react";
import ReactSelect from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { FormikLabel } from "./FormikInput";
import DropdownTreeSelect from "react-dropdown-tree-select";
import "react-dropdown-tree-select/dist/styles.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

function FormikSelect({
  name,
  label = "",
  disabled,
  isMulti,
  options = [],
  onChange,
  onChangeTreeSelect,
  isTree = false,
  handleLoadMore,
  handleSearch = false,
  handleCreate = false,
  placeholder,
  ...restProps
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [field, meta] = useField(name || "");
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const { value, onBlur, ...restFieldProps } = field;
  const { touched, error } = meta;

  const onChangeSelect = (selectedOption, event) => {
    // Do not change anything if custom button is clicked

    if (selectedOption?.value === -1) return;
    if (isMulti) {
      const result = selectedOption.some((item) => item.value === -1);
      if (result) return;
    }
    let fieldValue = "";
    if (isMulti) {
      fieldValue = [];
      selectedOption?.forEach((option) => fieldValue.push(option.value));
    } else {
      fieldValue = selectedOption ? selectedOption.value : "";
    }
    setFieldValue(name, fieldValue);
    if (onChange) onChange(selectedOption || {}, event);
  };

  const handleBlur = (event) => {
    setFieldTouched(name, true);
  };
  const handleLoadOptions = (name) => {
    if (handleSearch) {
      return new Promise((resolve) => {
        handleSearch({ name, callback: resolve });
      });
    }
    return new Promise((resolve) => {
      resolve(
        options?.filter((option) => {
          try {
            return option?.label
              ?.toLowerCase()
              ?.includes(name?.trim()?.toLowerCase());
          } catch (error) {
            return false;
          }
        })
      );
    });
  };
  const updatedOptions = useMemo(
    () =>
      handleLoadMore
        ? [
            ...options,

            {
              label: (
                <div
                  role="button"
                  onClick={handleLoadMore}
                  value={"load_more_button"}
                  data-bs-toggle="modal"
                  data-bs-target={`#${name}`}
                >
                  Load More...
                </div>
              ),
              value: -1,
            },
          ]
        : [...options],
    [handleLoadMore, options]
  );
  const selectedOption = useMemo(() => {
    let selectedValue = "";

    if (isMulti) {
      selectedValue = options.filter((option) => value?.includes(option.value));
    } else {
      selectedValue = options.find((option) => value === option.value);
    }

    return selectedValue;
  }, [value, updatedOptions]);
  return (
    <>
      {label && <FormikLabel name={name}>{label}</FormikLabel>}

      {
        isTree ? (
          <DropdownTreeSelect
            name={name}
            disabled={disabled}
            data={options}
            mode="hierarchical"
            onChange={(...props) =>
              onChangeTreeSelect(...props, { name, setFieldValue })
            }
            // searchPredicate={() => {
            //   console.log("first");
            // }}
            className="tree-select"
            texts={{
              placeholder: "Search or Select...",
            }}
            styles={{
              minHeight: "58px",
              borderRadius: "15px",
              border: "1px solid #CBCBCB",
              background: "#F5F5F5",
              boxShadow: "0 !important",
              "&:hover": {
                border: "1px solid #CBCBCB !important",
              },
            }}
          />
        ) : handleCreate ? (
          <AsyncCreatableSelect
            {...restProps}
            {...restFieldProps}
            name={name}
            isMulti={isMulti}
            cacheOptions
            className="react-select"
            isSearchable
            placeholder={placeholder}
            isLoading={isLoading}
            defaultOptions={updatedOptions}
            loadOptions={handleLoadOptions}
            onCreateOption={async (inputValue) => {
              try {
                setIsLoading(true);

                if (inputValue?.trim()?.length > 0) {
                  const response = await handleCreate(inputValue);
                  if (response) {
                    onChangeSelect(
                      isMulti ? [...selectedOption, response] : response,
                      {
                        action: "select-option",
                        name,
                        option: response,
                      }
                    );
                  }
                }
                setIsLoading(false);
              } catch (error) {
                console.log(error);
                setIsLoading(false);
              }
            }}
            isDisabled={isLoading || disabled}
            value={selectedOption || ""}
            onChange={onChangeSelect}
            onBlur={handleBlur}
            inputId={name}
            isClearable
            menuPosition="fixed"
            styles={{
              groupHeading: (provided) => ({
                ...provided,
                fontSize: "12px",
                fontWeight: 800,
                color: "#00000",
              }),
              option: (styles, { isDisabled }) => ({
                ...styles,
                paddingLeft: "20px !important",
                paddingTop: "5px",
                paddingBottom: "5px",
                fontSize: "13px",
                cursor: isDisabled ? "not-allowed" : "default",
                color: isDisabled ? "#aaa !important" : "#000",
              }),
              control: (baseStyles, state) => ({
                ...baseStyles,
                minHeight: "58px",
                borderRadius: "15px",
                border: "1px solid #CBCBCB",
                background: "#F5F5F5",
                boxShadow: "0 !important",
                "&:hover": {
                  border: "1px solid #CBCBCB !important",
                },
              }),
              ...restFieldProps.styles,
            }}
          />
        ) : (
          <AsyncSelect
            {...restProps}
            {...restFieldProps}
            name={name}
            isMulti={isMulti}
            cacheOptions
            className="react-select"
            isSearchable
            defaultOptions={updatedOptions}
            placeholder={placeholder}
            loadOptions={handleLoadOptions}
            isDisabled={disabled}
            value={selectedOption || ""}
            onChange={onChangeSelect}
            onBlur={handleBlur}
            inputId={name}
            isClearable
            menuPosition="fixed"
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                minHeight: "58px",
                borderRadius: "15px",
                border: "1px solid #CBCBCB",
                background: "#F5F5F5",
                boxShadow: "0 !important",
                "&:hover": {
                  border: "1px solid #CBCBCB !important",
                },
              }),
              ...restFieldProps.styles,
            }}
          />
        )
        // : (
        //   <ReactSelect
        //     {...restProps}
        //     {...restFieldProps}
        //     name={name}
        //     isMulti={isMulti}
        //     isDisabled={disabled}
        //     value={selectedOption || ""}
        //     onChange={onChangeSelect}
        //     isClearable
        //     className="react-select"
        //     onBlur={handleBlur}
        //     inputId={name}
        //     // menuIsOpen
        //     menuPosition="fixed"
        //     styles={{
        //       control: (baseStyles) => ({
        //         ...baseStyles,
        //         minHeight: "58px",
        //         borderRadius: "15px",
        //         border: "1px solid #CBCBCB",
        //         background: "#F5F5F5",
        //         boxShadow: "0 !important",
        //         "&:hover": {
        //           border: "1px solid #CBCBCB !important",
        //         },
        //       }),
        //       ...restFieldProps.styles,
        //     }}
        //     options={updatedOptions}
        //   />
        // )
      }
      {touched && error && <span className="ps-2 error-message">{error}</span>}
    </>
  );
}

export default FormikSelect;
